import React, { useEffect, useState, useContext } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import brLocale from 'date-fns/locale/pt-BR';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Button, Grid, TextField, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, Divider } from '@material-ui/core';
import Typography from "@material-ui/core/Typography";
import api from '../../services/api';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { makeStyles, useTheme } from "@material-ui/core/styles";
import './button.css';
import { i18n } from '../../translate/i18n';
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    container: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.padding,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(2),
    },

    chartFilter: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        margin: 0,
        marginBottom: theme.spacing(2),
        fontFamily: '"Poppins", sans-serif',
        color: theme.mode === 'light' ? '#1D1D1F' : '#c0cad9',
    },

        buttonFilter: {
        border: `1px solid ${theme.mode === 'light' ? '#e5e5e5' : '#1D1D1F'}`,
        color: theme.mode === 'light' ? '#1D1D1F' : '#c0cad9',
        lineHeight: 1,
    },
}));

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

export const ChartsSalesLine = () => {
    const classes = useStyles();
    const theme = useTheme();
    const [initialDate, setInitialDate] = useState(new Date());
    const [finalDate, setFinalDate] = useState(new Date());
    const [initialDate2, setInitialDate2] = useState(new Date());
    const [finalDate2, setFinalDate2] = useState(new Date());
    const [salesData, setSalesData] = useState([]);
    const [salesData2, setSalesData2] = useState([]);
    const [compare, setCompare] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const { user } = useContext(AuthContext);

    const companyId = user.companyId;

    useEffect(() => {
        if (companyId) {
            handleGetSalesInformation();
        }
    }, [companyId]);

    const handleGetSalesInformation = async () => {
        try {
            const { data } = await api.get(`/dashboard/salesTrend`, {
                params: {
                    initialDate: format(initialDate, 'yyyy-MM-dd'),
                    finalDate: format(finalDate, 'yyyy-MM-dd'),
                    companyId
                }
            });
            setSalesData(data);

            if (compare) {
                const { data: data2 } = await api.get(`/dashboard/salesTrend`, {
                    params: {
                        initialDate: format(initialDate2, 'yyyy-MM-dd'),
                        finalDate: format(finalDate2, 'yyyy-MM-dd'),
                        companyId
                    }
                });
                setSalesData2(data2);
            } else {
                setSalesData2([]);
            }
        } catch (error) {
            toast.error('Erro ao buscar informações de tendências de vendas');
        }
    }

    const maxLen = Math.max(salesData.length, salesData2.length);
    const labels = Array.from({length: maxLen}, (_, i) => {
        if (compare) return `Ponto ${i+1}`;
        return salesData[i] ? salesData[i].data : '';
    });

    const datasets = [
        {
            label: compare ? 'Faturamento (Período 1)' : 'Faturamento',
            data: Array.from({length: maxLen}, (_, i) => salesData[i]?.valor !== undefined ? salesData[i].valor : null),
            borderColor: theme.palette.primary.main,
            backgroundColor: `${theme.palette.primary.main}33`,
            fill: !compare,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
        }
    ];

    if (compare) {
        datasets.push({
            label: 'Faturamento (Período 2)',
            data: Array.from({length: maxLen}, (_, i) => salesData2[i]?.valor !== undefined ? salesData2[i].valor : null),
            borderColor: '#FF9800',
            backgroundColor: '#FF980033',
            fill: false,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
        });
    }

    const dataCharts = {
        labels,
        datasets,
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { display: compare },
            datalabels: { display: false }, // Oculta os rótulos de dados estáticos para não poluir a linha
            tooltip: {
                callbacks: {
                    title: (context) => {
                        const index = context[0].dataIndex;
                        if (compare) {
                            const d1 = salesData[index]?.data || 'N/A';
                            const d2 = salesData2[index]?.data || 'N/A';
                            return `Ponto ${index+1} (${d1} vs ${d2})`;
                        }
                        return salesData[index]?.data || '';
                    },
                    label: (context) => {
                        let label = context.dataset.label || '';
                        if (label) label += ': ';
                        if (context.parsed.y !== null) {
                            label += new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(context.parsed.y);
                        }
                        return label;
                    },
                    afterLabel: (context) => {
                        const index = context.dataIndex;
                        const isPeriod1 = context.datasetIndex === 0;
                        const vendas = isPeriod1 ? (salesData[index]?.vendas || 0) : (salesData2[index]?.vendas || 0);
                        return `Vendas Concluídas: ${vendas}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumSignificantDigits: 3 }).format(value)
                }
            }
        }
    };

    return (
        <>
            <div className={classes.chartFilter}>
                <Typography component="h2" variant="h6" gutterBottom style={{ margin: 0 }}>
                    Evolução de Vendas
                </Typography>
                <Button className={classes.buttonFilter} onClick={() => setOpenModal(true)}>
                    <i className="ri-filter-3-line" style={{ marginRight: 8 }}></i> Filtros
                </Button>
            </div>

            <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Filtros de Período</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                                <DatePicker
                                    value={initialDate}
                                    onChange={(newValue) => setInitialDate(newValue)}
                                    label={i18n.t("dashboard.date.initialDate")}
                                    renderInput={(params) => <TextField fullWidth {...params} />}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                                <DatePicker
                                    value={finalDate}
                                    onChange={(newValue) => setFinalDate(newValue)}
                                    label={i18n.t("dashboard.date.finalDate")}
                                    renderInput={(params) => <TextField fullWidth {...params} />}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>

                    <Divider style={{ margin: '24px 0' }} />

                    <FormControlLabel
                        control={<Checkbox checked={compare} onChange={(e) => setCompare(e.target.checked)} color="primary" />}
                        label="Comparar Períodos"
                    />

                    {!compare && (
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                            Data atual: {format(new Date(), 'dd/MM/yyyy')}
                        </Typography>
                    )}

                    {compare && (
                        <Grid container spacing={2} style={{ marginTop: 8 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth type="date" label="Data Inicial (Comparação)" InputLabelProps={{ shrink: true }} value={format(initialDate2, 'yyyy-MM-dd')} onChange={(e) => { if(e.target.value) setInitialDate2(new Date(e.target.value + 'T00:00:00')) }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth type="date" label="Data Final (Comparação)" InputLabelProps={{ shrink: true }} value={format(finalDate2, 'yyyy-MM-dd')} onChange={(e) => { if(e.target.value) setFinalDate2(new Date(e.target.value + 'T00:00:00')) }} />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: 16 }}>
                    <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
                    <Button onClick={() => { handleGetSalesInformation(); setOpenModal(false); }} color="primary" variant="contained" style={{ color: '#fff' }}>
                        Aplicar Filtro
                    </Button>
                </DialogActions>
            </Dialog>
            
            <Line options={options} data={dataCharts} style={{ maxWidth: '100%', maxHeight: '280px', marginTop: '16px' }} />
        </>
    );
}
export default ChartsSalesLine;